/**
 * Pure date helpers, pt-BR aware. Intentionally no `date-fns` dependency
 * so this file stays trivially testable and tree-shake-cheap.
 */

const SHORT_MONTHS_PT_BR = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
] as const;

const SHORT_WEEKDAYS_PT_BR = [
  'dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb',
] as const;

const MS_PER_DAY = 86_400_000;

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}(?:T00:00:00(?:\.0+)?Z)?$/;

/**
 * A bare date, or midnight UTC (how Pluggy ships date-only card charges), is a calendar date
 * with no time of day — never an instant to shift into the local zone, which would move it to
 * the previous evening west of GMT.
 */
export function isCalendarDate(iso: string): boolean {
  return DATE_ONLY_RE.test(iso);
}

/**
 * A transaction timestamp as the person sees it: a calendar date is local midnight of that day
 * with no time; anything else is the instant it names, and has a time to show.
 */
export function transactionDate(iso: string): { date: Date; hasTime: boolean } {
  if (isCalendarDate(iso)) return { date: new Date(`${iso.slice(0, 10)}T00:00:00`), hasTime: false };
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return { date: new Date(`${iso.slice(0, 10)}T00:00:00`), hasTime: false };
  return { date, hasTime: true };
}

/**
 * The `yyyy-mm-dd` day a transaction timestamp belongs to — the LOCAL day of an instant, so a
 * Pix sent at 22:04 in Brasília (01:04 UTC the next day) lands on the day it was sent, and the
 * calendar day itself for a date-only value. The same reading `transactionDate` renders.
 */
export function localDayKey(iso: string): string {
  const { date } = transactionDate(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function shortMonth(monthIndex: number): string {
  if (monthIndex < 0 || monthIndex > 11) return '';
  return SHORT_MONTHS_PT_BR[monthIndex]!;
}

export function daysBetween(a: Date, b: Date): number {
  const sa = startOfDay(a).getTime();
  const sb = startOfDay(b).getTime();
  return Math.round((sa - sb) / MS_PER_DAY);
}

export function dayLabel(date: Date, now: Date = new Date()): string {
  const diff = daysBetween(now, date);
  if (diff === 0) return 'Hoje';
  if (diff === 1) return 'Ontem';
  const wd = SHORT_WEEKDAYS_PT_BR[date.getDay()]!;
  const label = `${wd}, ${date.getDate()} ${shortMonth(date.getMonth())}`;
  // Append the year when the date isn't in the current year, so entries from
  // other years (e.g. a transaction from a previous year) aren't ambiguous —
  // `qua, 6 mai 2024` instead of a bare `qua, 6 mai`.
  return date.getFullYear() === now.getFullYear() ? label : `${label} ${date.getFullYear()}`;
}

/** Signed integer days from `now` to `target`. Positive = future, negative = past. */
export function daysTo(target: Date, now: Date = new Date()): number {
  return daysBetween(target, now);
}

/** pt-BR short month label for a Date — `nov`, `set`. */
export function shortMonthLabel(date: Date): string {
  return shortMonth(date.getMonth());
}

/**
 * pt-BR countdown phrase relative to a fatura due date.
 * `paid` overrides — used when `status === 'paid'`.
 */
export function dueLabel(
  dueDate: Date,
  options: { now?: Date; paid?: boolean } = {},
): string {
  const now = options.now ?? new Date();
  const diff = daysTo(dueDate, now);
  if (options.paid) {
    if (diff > 0) return `Paga`;
    if (diff === 0) return `Paga hoje`;
    return `Paga há ${Math.abs(diff)}d`;
  }
  if (diff > 0) return `Vence em ${diff}d`;
  if (diff === 0) return 'Vence hoje';
  return `Venceu há ${Math.abs(diff)}d`;
}
